<template>
  <b-message
      :closable="true"
      v-model="show"
      :type="type"
      :aria-close-label="$t('close')"
      class="osv-app-alert"
      v-bind:class="osvAlertPosition"
      :title="title"
  >
    <p class="mb-0" v-for="(message, index) of messages" :key="index">
      {{ message }}
    </p>
  </b-message>
</template>

<script>
import VueI18n from "vue-i18n";
import { LocaleMixin } from "@/mixins/LocaleMixin";

export default {
  name: "OsvAppAlert",
  mixins: [LocaleMixin],
  props: {
    osvAlertPosition: {
      type: String,
      default: 'osv-alert-fixed'
    },
    scrolling: {
      type: Boolean,
      default: true
    },
    directError: {
      type: Object,
      default: null
    }
  },

  watch: {
    directError: function () {
      if (this.directError) {
        if (this.directError.event === 'OsvAlert_ErrorFromApi') {
          this.fromApiError(this.directError.err, this.directError.object);
        }
        if (this.directError.event === 'OsvAlert_SimpleAlert') {
          this.fromApiError(this.directError.message);
        }
        if (this.directError.event === 'OsvAlert_SimpleSuccess') {
          this.fromApiError(this.directError.message);
        }
      }
    }
  },

  data() {
    return {
      title: null,
      show: false,
      type: "error",
      messages: []
    };
  },

  mounted() {
    this.$i18n.locale = this.getLocale();

    this.$root.$on("OsvAlert_ErrorFromApi", (err, object) => {
      this.fromApiError(err, object);
    });
    this.$root.$on("OsvAlert_SimpleAlert", message => {
      this.simpleError(message);
    });
    this.$root.$on("OsvAlert_SimpleSuccess", message => {
      this.successAlert(message);
    });
  },

  beforeDestroy() {
    this.$root.$off("OsvAlert_ErrorFromApi");
    this.$root.$off("OsvAlert_SimpleAlert");
    this.$root.$off("OsvAlert_SimpleSuccess");
  },

  methods: {
    fromApiError: function(err, object) {
      if (this.scrolling) this.scrollToTop();
      const errors = err.response.data;
      const status = err.response.status;
      let errorCount = 0;

      this.clear();
      if (status === 500 || status === 400 || status === 401) {
        this.messages.push(this.$t(`alert.${status}`));
      } else if (status === 422) {
        // count errors
        for (const error in errors) {
          for (const rule of errors[error]) {
            errorCount++;
          }
        }

        // Create error message array
        for (const error in errors) {
          let item = "";
          if (error !== "base") item = this.$t(`models.${object}.${error}`);
          for (const rule of errors[error]) {
            this.messages.push(`${errorCount > 1 ? '- ' : ''}${item} ${rule}`);
          }
        }

        // Add title if I have more than one error
        this.title = (errorCount > 1) ? this.$t("alert.422") : null;
      } else {
        this.messages.push(this.$t("alert.500"));
      }
      this.show = true;
      this.type = "is-danger";
    },

    simpleError: function(message) {
      this.simpleAlert(message, "is-danger");
    },

    successAlert: function(message) {
      this.simpleAlert(message, "is-success");
    },

    simpleAlert: function(message, type) {
      this.scrollToTop();
      this.clear();
      this.messages.push(message);
      this.show = true;
      this.type = type;
    },

    clear: function() {
      this.title = null;
      this.messages = [];
    },

    scrollToTop: function () {
      if (this.osvAlertPosition === 'osv-alert-relative') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    }
  },

  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        close: "Close message",
        alert: {
          "500": "An unexpected error occurred, please try again later.",
          "400":
            "Items seem to be missing, please fill in the following fields",
          "401": "You are not authorized to do this action.",
          "422": "One of the following items does not appear to be correct :"
        },
        models: {
          recruitment: {
            candidate: {
              firstname: "First name",
              lastname: "Last name",
              email: "Email",
              phone: "Phone"
            },
            cv: "CV",
            letter_of_motivation: "Letter of motivation"
          },
          customer: {
            password: "Password",
            confirm_password: "Confirmed password",
            email: "Email",
            lastname: "Last name",
            firstname: "First name",
            date_of_birth: "Date of birth",
            phone: "Phone",
            address: "Address",
            city: "City",
            zipcode: "Postal code"
          },
          cart: {
            email: "Email",
            lastname: "Last name",
            firstname: "First name",
            date_of_birth: "Date of birth",
            amount: "Invoice amount"
          },
          online_account: {
            email: 'Email',
            reset_password_token: "reset token",
            password: "Password",
            password_confirmation: "Password confirmation"
          },
          newsletter_subscribe: {
            email: 'email'
          },
          subscription: {
            start_date: 'New date'
          }
        }
      },
      fr: {
        close: "Fermer le message",
        alert: {
          "500":
            "Une erreur inattendue s'est produite, merci d'essayer plus tard.",
          "400":
            "Des éléments semblent manquer, merci de renseigner les champs.",
          "401": "Vous n'êtes pas autorisé·e à faire cette action",
          "422": "L'un des éléments suivants ne semble pas correct :"
        },
        models: {
          recruitment: {
            candidate: {
              firstname: "Nom",
              lastname: "Prénoms",
              email: "Email",
              phone: "Téléphone"
            },
            cv: "CV",
            letter_of_motivation: "Lettre de motivation"
          },
          customer: {
            password: "Mot de passe",
            confirm_password: "Mot de passe confirmé",
            email: "Email",
            lastname: "Nom",
            firstname: "Prénom",
            date_of_birth: "Date de naissance",
            phone: "Téléphone",
            address: "Adresse",
            city: "Ville",
            zipcode: "Code postal"
          },
          cart: {
            email: "Email",
            lastname: "Nom",
            firstname: "Prénom",
            date_of_birth: "Date de naissance",
            amount: "Montant de la facture"
          },
          online_account: {
            email: 'Email',
            reset_password_token: "Le jeton de résiliation",
            password: "Le mot de passe",
            password_confirmation: "Le mot de passe de confirmation"
          },
          newsletter_subscribe: {
            email: 'email'
          },
          subscription: {
            start_date: 'Date de début'
          }
        }
      }
    }
  })
};
</script>

<style lang="scss">
.osv-app-alert {
  &.osv-alert-fixed {
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 100;
  }
  &.osv-alert-relative {
    position: relative;
    margin-bottom: 10px;
    margin-top: 10px;
  }
  &.message.is-danger .message-body {
    color: rgb(204, 15, 53);
  }
}
</style>
